import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/components/ui/use-toast";

interface UseFullscreenProps {
  onFullscreenChange?: (isFullscreen: boolean, event: 'enter' | 'exit' | 'escape_key') => void;
  onSecurityViolation?: (type: 'unauthorized_exit' | 'escape_key_used', details: string) => void;
  onAddViolation?: (type: 'fullscreen_exit' | 'fullscreen_escape_key', message: string, severity: 'warning' | 'penalty' | 'critical') => void;
  enableViolationTracking?: boolean;
  isInterviewActive?: boolean;
}

interface UseFullscreenReturn {
  isFullscreen: boolean;
  isSupported: boolean;
  requestFullscreen: (element?: HTMLElement, retryCount?: number) => Promise<boolean>;
  exitFullscreen: () => Promise<boolean>;
  toggleFullscreen: (element?: HTMLElement) => Promise<boolean>;
  forceExitCheck: () => void;
}

export const useFullscreen = (props: UseFullscreenProps = {}): UseFullscreenReturn => {
  const { 
    onFullscreenChange, 
    onSecurityViolation, 
    onAddViolation,
    enableViolationTracking = false,
    isInterviewActive = false 
  } = props;
  
  const { toast } = useToast();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSupported] = useState(() => {
    return !!(
      document.documentElement.requestFullscreen ||
      (document.documentElement as any).webkitRequestFullscreen ||
      (document.documentElement as any).mozRequestFullScreen ||
      (document.documentElement as any).msRequestFullscreen
    );
  });

  const isRequestingRef = useRef(false);
  const lastExitEventRef = useRef<'user_action' | 'escape_key' | 'unknown'>('unknown');
  const escKeyListenerRef = useRef<((e: KeyboardEvent) => void) | null>(null);

  // Monitor fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      
      const eventType = isCurrentlyFullscreen ? 'enter' : 
                       lastExitEventRef.current === 'escape_key' ? 'escape_key' : 'exit';
      
      // Fullscreen state changed; propagate via callback and enforce security checks
      
      // Security violation detection for unauthorized exits during interviews
      if (!isCurrentlyFullscreen && isInterviewActive && enableViolationTracking) {
        const violationType = lastExitEventRef.current === 'escape_key' ? 'escape_key_used' : 'unauthorized_exit';
        const details = `Fullscreen exited during interview via ${lastExitEventRef.current}. This may indicate an attempt to bypass proctoring.`;
        
        // Call the original security violation handler for backwards compatibility
        onSecurityViolation?.(violationType, details);
        
        // Also integrate with the main violation tracking system
        const violationTypeForSystem = lastExitEventRef.current === 'escape_key' ? 'fullscreen_escape_key' : 'fullscreen_exit';
        const violationMessage = lastExitEventRef.current === 'escape_key' 
          ? 'ESC key used to exit fullscreen during interview'
          : 'Unauthorized fullscreen exit detected during interview';
        const severity = lastExitEventRef.current === 'escape_key' ? 'warning' : 'critical';
        
        onAddViolation?.(violationTypeForSystem, violationMessage, severity);
      }
      
      setIsFullscreen(isCurrentlyFullscreen);
      isRequestingRef.current = false;
      
      // Notify parent component of fullscreen state change
      onFullscreenChange?.(isCurrentlyFullscreen, eventType);
      
      // Reset exit event tracking
      lastExitEventRef.current = 'unknown';
    };

    // ESC key detection for security tracking
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen && enableViolationTracking) {
        lastExitEventRef.current = 'escape_key';
      }
    };

    // Add event listeners for all browser variants
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    // Add ESC key detection if violation tracking is enabled
    if (enableViolationTracking) {
      document.addEventListener('keydown', handleEscKey, { capture: true });
      escKeyListenerRef.current = handleEscKey;
    }

    // Check initial state
    handleFullscreenChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      
      if (escKeyListenerRef.current) {
        document.removeEventListener('keydown', escKeyListenerRef.current, { capture: true });
        escKeyListenerRef.current = null;
      }
    };
  }, [enableViolationTracking, isFullscreen, isInterviewActive, onFullscreenChange, onSecurityViolation]);

  const requestFullscreen = useCallback(async (element?: HTMLElement, retryCount = 0): Promise<boolean> => {
    if (!isSupported) {
      toast({
        title: "Fullscreen Not Supported",
        description: "Your browser doesn't support fullscreen mode.",
        variant: "destructive",
      });
      return false;
    }

    if (isRequestingRef.current) {
      return false;
    }

    if (isFullscreen) {
      return true;
    }

    const target = element || document.documentElement;
    isRequestingRef.current = true;

    try {
      
      // Try different browser-specific methods with Chrome optimization
      if (target.requestFullscreen) {
        await target.requestFullscreen();
      } else if ((target as any).webkitRequestFullscreen) {
        // For Chrome/Safari - try with flags for better compatibility
        await (target as any).webkitRequestFullscreen((Element as any).ALLOW_KEYBOARD_INPUT);
      } else if ((target as any).mozRequestFullScreen) {
        await (target as any).mozRequestFullScreen();
      } else if ((target as any).msRequestFullscreen) {
        await (target as any).msRequestFullscreen();
      } else {
        throw new Error("No fullscreen method available");
      }

      return true;
    } catch (error) {
      isRequestingRef.current = false;
      
      // Handle specific error cases with retry logic for Windows Chrome
      if (error instanceof Error) {
        if (error.name === "NotAllowedError" && retryCount < 2) {
          // Retry immediately for NotAllowedError (common in Windows Chrome)
          await new Promise(resolve => setTimeout(resolve, 100));
          return requestFullscreen(element, retryCount + 1);
        } else if (error.name === "NotAllowedError") {
          toast({
            title: "Fullscreen Permission Denied",
            description: "Please allow fullscreen mode for the best interview experience. You may need to click the 'Start Interview' button again.",
          });
        } else if (error.name === "TypeError") {
          toast({
            title: "Fullscreen Not Available",
            description: "Fullscreen mode is not available. The interview will continue in windowed mode.",
          });
        } else {
          toast({
            title: "Fullscreen Failed",
            description: "Unable to enter fullscreen mode. The interview will continue normally.",
          });
        }
      }
      
      return false;
    }
  }, [isSupported, isFullscreen, toast]);

  const exitFullscreen = useCallback(async (): Promise<boolean> => {
    if (!isFullscreen) {
      return true;
    }

    try {
      lastExitEventRef.current = 'user_action';
      
      // Try different browser-specific methods
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      } else {
        throw new Error("No exit fullscreen method available");
      }

      return true;
    } catch (error) {
      toast({
        title: "Exit Fullscreen Failed",
        description: "Unable to exit fullscreen mode. Please press ESC key.",
        variant: "destructive",
      });
      return false;
    }
  }, [isFullscreen, toast]);

  const toggleFullscreen = useCallback(async (element?: HTMLElement): Promise<boolean> => {
    if (isFullscreen) {
      return await exitFullscreen();
    } else {
      return await requestFullscreen(element);
    }
  }, [isFullscreen, requestFullscreen, exitFullscreen]);

  // Force exit check - used by parent components to trigger violation detection
  const forceExitCheck = useCallback(() => {
    const currentlyFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );
    
    if (!currentlyFullscreen && isInterviewActive && enableViolationTracking) {
      lastExitEventRef.current = 'unknown';
      
      // Call the original security violation handler for backwards compatibility
      onSecurityViolation?.('unauthorized_exit', 'Fullscreen exit detected during interview security check');
      
      // Also integrate with the main violation tracking system
      onAddViolation?.('fullscreen_exit', 'Fullscreen exit detected during interview security check', 'critical');
    }
  }, [isInterviewActive, enableViolationTracking, onSecurityViolation, onAddViolation]);

  return {
    isFullscreen,
    isSupported,
    requestFullscreen,
    exitFullscreen,
    toggleFullscreen,
    forceExitCheck,
  };
};
